/**
 * Aiphra — Supabase query layer
 * All database access goes through these typed functions.
 * Components import from here, never from supabase.ts directly.
 */
import { supabase } from './supabase';
import type { Database } from './database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];
export type WalletTx   = Database['public']['Tables']['wallet_transactions']['Row'];
export type Purchase   = Database['public']['Tables']['purchases']['Row'];
export type Notification = Database['public']['Tables']['notifications']['Row'];
export type PromoCode  = Database['public']['Tables']['promo_codes']['Row'];

// Untyped proxy — used where Supabase generated types resolve to `never`
// due to missing table definitions or Supabase JS type-inference bugs.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// ============================================================
// PROFILES
// ============================================================

export async function getProfile(id: string): Promise<Profile | null> {
  const { data } = await db
    .from('profiles')
    .select('*')
    .eq('id', id)
    .single();
  return data;
}

export async function upsertProfile(profile: Database['public']['Tables']['profiles']['Insert']) {
  return db.from('profiles').upsert(profile, { onConflict: 'id' });
}

export async function setSellerStatus(id: string, isSeller: boolean) {
  return db.from('profiles').update({ is_seller: isSeller }).eq('id', id);
}

/** One-time post-signup step: record the plan they picked (or skipped) so we never ask again. */
export async function completeOnboarding(id: string, tier: 'free' | 'starter' | 'pro' | 'elite') {
  return db.from('profiles').update({ subscription_tier: tier, onboarding_completed: true }).eq('id', id);
}

/** Returns true if `username` is already taken by a different profile. */
export async function isUsernameTaken(username: string, currentUserId: string): Promise<boolean> {
  const { data } = await db
    .from('profiles')
    .select('id')
    .eq('username', username)
    .neq('id', currentUserId)
    .maybeSingle();
  return !!data;
}

export async function getSellerProfile(username: string): Promise<Profile | null> {
  const { data } = await db
    .from('profiles')
    .select('*')
    .eq('username', username)
    .single();
  return data;
}

// ============================================================
// PRODUCTS
// ============================================================

export async function getProducts({
  category,
  search,
  sort = 'sales_count',
  limit = 24,
  offset = 0,
}: {
  category?: string;
  search?: string;
  sort?: 'sales_count' | 'rating_avg' | 'price' | 'created_at';
  limit?: number;
  offset?: number;
} = {}) {
  let q = db
    .from('products')
    .select(`
      id, seller_id, title, slug, tagline, description, category, tags,
      price, is_free, demo_url, delivery_method, version, badge_text, status,
      pricing_model, seat_tiers, usage_price, usage_unit, free_units,
      sales_count, view_count, rating_avg, rating_count,
      is_featured, is_ai_showcase, featured_until, showcase_until, created_at,
      seller:profiles!seller_id (id, username, display_name, avatar_url, subscription_tier, seller_verified),
      images:product_images (url, position)
    `)
    .eq('status', 'published')
    .order('is_featured', { ascending: false })
    .order(sort, { ascending: sort === 'price' })
    .range(offset, offset + limit - 1);

  if (category) q = q.eq('category', category);

  if (search) {
    // Substring ILIKE instead of FTS (search_vector): FTS depends on the
    // tsvector trigger/index being present & populated (unreliable), and
    // websearch_to_tsquery chokes on special characters (TC-034). Strip chars
    // that would break PostgREST's or()/ilike syntax, then match title/tagline/
    // description.
    const term = search.replace(/[%,()*]/g, ' ').trim();
    if (term) {
      q = q.or(`title.ilike.%${term}%,tagline.ilike.%${term}%,description.ilike.%${term}%`);
    }
  }

  const { data, error } = await q;
  return { data: data ?? [], error };
}

export async function getProductBySlug(slug: string) {
  const { data, error } = await db
    .from('products')
    .select(`
      id, seller_id, title, slug, tagline, description, category, tags,
      price, is_free, demo_url, delivery_method, version, badge_text, status,
      pricing_model, seat_tiers, usage_price, usage_unit, free_units,
      sales_count, view_count, rating_avg, rating_count,
      is_featured, is_ai_showcase, featured_until, showcase_until, created_at, updated_at,
      setup_guide, whats_included, compatibility_badges, changelog,
      seller:profiles!seller_id (id, username, display_name, avatar_url, subscription_tier, seller_verified),
      images:product_images (url, position),
      files:product_files (id, filename, size_bytes, version)
    `)
    .eq('slug', slug)
    .single();
  return { data, error };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function getSellerProducts(sellerId: string): Promise<{ data: any[]; error: unknown }> {
  const { data, error } = await db
    .from('products')
    .select('*, images:product_images (url, position)')
    .eq('seller_id', sellerId)
    .order('created_at', { ascending: false });
  // Dashboard list pages read `preview_image_urls`, but products keep images in
  // the joined product_images table. Derive it (always an array) so pages don't
  // crash on `preview_image_urls[0]` for products that have no images.
  const mapped = (data ?? []).map((p: any) => ({
    ...p,
    preview_image_urls: (p.images ?? [])
      .slice()
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      .map((image: any) => image.url),
  }));
  return { data: mapped, error };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export async function incrementProductView(productId: string) {
  return db.rpc('increment', { table: 'products', column: 'view_count', id: productId });
}

export async function createProduct(product: Database['public']['Tables']['products']['Insert']) {
  return db.from('products').insert(product).select().single();
}

export async function updateProduct(id: string, updates: Database['public']['Tables']['products']['Update']) {
  return db.from('products').update(updates).eq('id', id).select().single();
}

// ============================================================
// PURCHASES
// ============================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getBuyerPurchases(buyerId: string): Promise<{ data: any[]; error: unknown }> {
  const { data, error } = await db
    .from('purchases')
    .select(`
      *,
      product:products (id, title, slug, delivery_method, setup_guide, images:product_images(url, position), files:product_files(id, storage_path, filename), external_delivery:product_external_deliveries(url)),
      seller:profiles!seller_id (username, display_name)
    `)
    .eq('buyer_id', buyerId)
    .order('purchased_at', { ascending: false });
  return { data: data ?? [], error };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getSellerSales(sellerId: string): Promise<{ data: any[]; error: unknown }> {
  const { data, error } = await db
    .from('purchases')
    .select(`
      *,
      product:products (id, title, slug),
      buyer:profiles!buyer_id (username, display_name)
    `)
    .eq('seller_id', sellerId)
    .order('purchased_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function hasPurchased(buyerId: string, productId: string): Promise<boolean> {
  const { count } = await db
    .from('purchases')
    .select('id', { count: 'exact', head: true })
    .eq('buyer_id', buyerId)
    .eq('product_id', productId)
    .eq('status', 'completed');
  return (count ?? 0) > 0;
}

// ============================================================
// REVIEWS
// ============================================================

export async function getProductReviews(productId: string) {
  const { data, error } = await db
    .from('reviews')
    .select(`
      *,
      buyer:profiles!buyer_id (username, display_name, avatar_url)
    `)
    .eq('product_id', productId)
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function createReview(review: Database['public']['Tables']['reviews']['Insert']) {
  return db.from('reviews').insert(review).select().single();
}

export async function replyToReview(reviewId: string, reply: string) {
  return db.from('reviews')
    .update({ seller_reply: reply, seller_reply_at: new Date().toISOString() })
    .eq('id', reviewId);
}

// ============================================================
// WALLET
// ============================================================

export async function getWalletTransactions(userId: string): Promise<{ data: WalletTx[]; error: unknown }> {
  const { data, error } = await db
    .from('wallet_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return { data: (data ?? []) as WalletTx[], error };
}

// ============================================================
// NOTIFICATIONS
// ============================================================

export async function getNotifications(userId: string): Promise<{ data: Notification[]; error: unknown }> {
  const { data, error } = await db
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  return { data: (data ?? []) as Notification[], error };
}

export async function markNotificationRead(id: string) {
  return db.from('notifications').update({ is_read: true }).eq('id', id);
}

export async function markAllNotificationsRead(userId: string) {
  return db.from('notifications').update({ is_read: true }).eq('user_id', userId);
}

// ============================================================
// WISHLIST
// ============================================================

export async function getWishlist(userId: string) {
  const { data, error } = await db
    .from('wishlist_items')
    .select(`
      *,
      product:products (
        id, title, slug, price, is_free, rating_avg, category, badge_text,
        seller:profiles!seller_id (username),
        images:product_images (url, position)
      )
    `)
    .eq('user_id', userId);
  return { data: data ?? [], error };
}

export async function addToWishlist(userId: string, productId: string) {
  return db.from('wishlist_items').upsert({ user_id: userId, product_id: productId });
}

export async function removeFromWishlist(userId: string, productId: string) {
  return db.from('wishlist_items').delete().eq('user_id', userId).eq('product_id', productId);
}

// ============================================================
// PROMO CODES
// ============================================================

export async function validatePromoCode(sellerId: string, code: string) {
  const { data } = await db.from('promo_codes')
    .select('*')
    .eq('seller_id', sellerId)
    .eq('code', code.toUpperCase())
    .eq('active', true)
    .single();
  if (!data) return null;
  const row = data as Database['public']['Tables']['promo_codes']['Row'];
  if (row.max_uses !== null && row.uses >= row.max_uses) return null;
  if (row.expires_at && new Date(row.expires_at) < new Date()) return null;
  return row;
}

export async function getSellerPromoCodes(sellerId: string) {
  const { data, error } = await db.from('promo_codes')
    .select('*')
    .eq('seller_id', sellerId)
    .order('created_at', { ascending: false });
  return { data: (data ?? []) as Database['public']['Tables']['promo_codes']['Row'][], error };
}

// ============================================================
// DISPUTES
// ============================================================

export async function getDisputes(userId: string) {
  const { data, error } = await db
    .from('disputes')
    .select(`
      *,
      purchase:purchases (product_id, discounted_price),
      product:purchases!purchase_id (products (title))
    `)
    .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function createDispute(dispute: Database['public']['Tables']['disputes']['Insert']) {
  return db.from('disputes').insert(dispute).select().single();
}

// ============================================================
// STORAGE HELPERS
// ============================================================

/** Upload a product preview image. Returns the public URL. */
export async function uploadProductImage(
  sellerId: string,
  productId: string,
  file: File
): Promise<string | null> {
  const ext  = file.name.split('.').pop();
  const path = `${sellerId}/${productId}/${Date.now()}.${ext}`;

  const { error } = await supabase.storage
    .from('product-previews')
    .upload(path, file, { upsert: false });

  if (error) return null;

  const { data } = supabase.storage.from('product-previews').getPublicUrl(path);
  return data.publicUrl;
}

/** Upload a product file (private). Returns the storage path. */
export async function uploadProductFile(
  sellerId: string,
  productId: string,
  file: File
): Promise<string | null> {
  const path = `${sellerId}/${productId}/${file.name}`;

  const { error } = await supabase.storage
    .from('product-files')
    .upload(path, file, { upsert: true });

  return error ? null : path;
}

/** Create a signed URL for a private product file (valid 1 hour). */
export async function getProductFileUrl(storagePath: string): Promise<string | null> {
  const { data } = await db.storage
    .from('product-files')
    .createSignedUrl(storagePath, 3600);
  return data?.signedUrl ?? null;
}

/** Upload a user avatar. Returns { url, error } so the UI can show the real reason. */
export async function uploadAvatar(userId: string, file: File): Promise<{ url: string | null; error: string | null }> {
  const ext  = file.name.split('.').pop();
  const path = `${userId}/avatar.${ext}`;

  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type || undefined });

  if (error) {
    console.error('[avatar upload]', error);
    return { url: null, error: error.message };
  }

  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  // Re-uploading always writes to the same {userId}/avatar.{ext} path, so the
  // public URL string is identical between uploads — browsers (and Supabase's
  // own CDN) then just serve the OLD cached bytes at that URL instead of
  // fetching the new file. A cache-busting query param forces a fresh fetch.
  return { url: `${data.publicUrl}?v=${Date.now()}`, error: null };
}
